"use client"
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";
import FAQS from "../components/shared/FAQS";
import FormsAndDocuments from "../components/shared/FormsAndDocuments";
import Header from "../components/shared/Header";
import ProfileForm from "../components/shared/PackageCalculator";
import TestimonialSection from "../components/shared/Testimonial";

import EmailButton from "../components/shared/EmailButton";
import WhatsappButton from "../components/shared/WhatsappButton";

import CustomersMarquee from "../components/shared/CustomersMarquee";
import PartnersSection from "../components/shared/PartnersSection";
import { useSessionContext } from '../context/UserContext';

const Home = () => {
  const { session, isPending } = useSessionContext();
  
  // State for database data
  const [services, setServices] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [howItWorksSteps, setHowItWorksSteps] = useState([]);
  const [pricingRows, setPricingRows] = useState([]);
  const [pricingService, setPricingService] = useState("air");
  const [aboutSpecializations, setAboutSpecializations] = useState([]);
  const [shippingDestinations, setShippingDestinations] = useState([]);
  const [heroContent, setHeroContent] = useState({
    title: "Seamless Shipping Between US & Caribbean",
    titleHighlight: "",
    subtitle:
      "End-to-end import and freight forwarding — packing, consolidation, customs clearance, and dependable door-to-door delivery across the Caribbean and beyond.",
    ctaText: "Get a Quote",
    ctaTextLoggedIn: "Go to Dashboard",
  });
  const [dataLoading, setDataLoading] = useState(true);
  const [promotions, setPromotions] = useState([]);

  // Fetch data from API on component mount
  useEffect(() => {
    async function fetchData() {
      try {
        const [
          servicesRes,
          addressesRes,
          benefitsRes,
          testimonialsRes,
          faqsRes,
          howItWorksRes,
          aboutSpecializationsRes,
          shippingDestinationsRes,
          heroRes,
        ] = await Promise.all([
          fetch('/api/services'),
          fetch('/api/addresses'),
          fetch('/api/benefits'),
          fetch('/api/testimonials'),
          fetch('/api/faqs'),
          fetch('/api/how-it-works'),
          // pricing will be fetched separately based on selected service
          fetch('/api/about?type=specializations'),
          fetch('/api/about?type=destinations'),
          fetch('/api/hero'),
        ]);
        
        const servicesData = await servicesRes.json();
        const addressesData = await addressesRes.json();
        const benefitsData = await benefitsRes.json();
        const testimonialsData = await testimonialsRes.json();
        const faqsData = await faqsRes.json();
        const howItWorksData = await howItWorksRes.json();
        // (pricing fetched below)
        const aboutSpecializationsData = await aboutSpecializationsRes.json();
        const shippingDestinationsData = await shippingDestinationsRes.json();
        const heroData = heroRes ? await heroRes.json() : { success: false };
        const promoRes = await fetch('/api/promo');
        const promoData = await promoRes.json();
        
        if (servicesData.success) setServices(servicesData.data);
        if (addressesData.success) setAddresses(addressesData.data);
        if (benefitsData.success) setBenefits(benefitsData.data);
        if (testimonialsData.success) setTestimonials(testimonialsData.data);
        if (faqsData.success) setFaqs(faqsData.data);
        if (howItWorksData.success) setHowItWorksSteps(howItWorksData.data);
        
        // fetch default pricing (service = pricingService)
        try {
          const pr = await fetch(`/api/pricing?service=${pricingService}`);
          const prData = await pr.json();
          if (prData.success) setPricingRows(prData.data);
        } catch (e) { console.error('Failed to load pricing', e); }
        if (promoData.success) setPromotions(promoData.data);{
          console.log("Fetched promotions:", promoData.data);
        }
        if (aboutSpecializationsData.success) setAboutSpecializations(aboutSpecializationsData.data);
        if (shippingDestinationsData.success) setShippingDestinations(shippingDestinationsData.data);
        if (heroData.success && heroData.data) setHeroContent(heroData.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setDataLoading(false);
      }
    }
    
    fetchData();
  }, []);

  // helper to switch pricing service (air/sea)
  const switchPricingService = async (service) => {
    setPricingService(service);
    try {
      const res = await fetch(`/api/pricing?service=${service}`);
      const data = await res.json();
      if (data.success) setPricingRows(data.data);
    } catch (err) { console.error('Failed to fetch pricing', err); }
  };

  const DEFAULT_PUBLIC_PRICING = [
    { _id: "pub-air-1", weight: "Barrels — up to 250 lbs.", cost: "$255", serviceType: "air", service: "Port-to-Port", isHighlighted: false },
    { _id: "pub-air-2", weight: "E-Containers — up to 250 lbs.", cost: "$280", serviceType: "air", service: "Port-to-Port", isHighlighted: false },
    { _id: "pub-air-3", weight: "D-Containers — up to 660 lbs.", cost: "$630", serviceType: "air", service: "Port-to-Port", isHighlighted: true },
    { _id: "pub-air-4", weight: "Pickup specials in Ft Lauderdale", cost: "$40 per location", serviceType: "air", service: "Pickup Fee", isHighlighted: false },
    { _id: "pub-sea-1", weight: "Barrels", cost: "$75", serviceType: "sea", service: "Port-to-Port", isHighlighted: false },
    { _id: "pub-sea-2", weight: "E-Containers", cost: "$200", serviceType: "sea", service: "Port-to-Port", isHighlighted: false },
    { _id: "pub-sea-3", weight: "D-Containers", cost: "$450", serviceType: "sea", service: "Port-to-Port", isHighlighted: true },
    { _id: "pub-sea-4", weight: "Pickup specials in Ft Lauderdale", cost: "$35-$50 per location", serviceType: "sea", service: "Pickup Fee", isHighlighted: false },
  ];

  if (isPending || dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="bg-[url('https://pagedone.io/asset/uploads/1691055810.png')] bg-center bg-cover">
      <Header />


      <section className="pt-32 ">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative text-center">
            <div className="absolute right-6 top-6 hidden sm:flex items-center gap-3 z-20">
              <a
                href="https://instagram.com/yourhandle"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80 shadow hover:scale-110 transition-transform duration-200 motion-reduce:transform-none"
              >
                <svg className="w-6 h-6 text-pink-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.2"/>
                  <circle cx="17.5" cy="6.5" r="0.7" fill="currentColor"/>
                </svg>
              </a>

              <a
                href="https://www.tiktok.com/@yourhandle"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80 shadow hover:scale-110 transition-transform duration-200 motion-reduce:transform-none"
              >
                <svg className="w-6 h-6 text-black" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M17.5 7.75c-1.1 0-2.05-.38-2.82-1.02v6.54c0 1.9-1.55 3.45-3.45 3.45-1.9 0-3.45-1.55-3.45-3.45 0-1.9 1.55-3.45 3.45-3.45.3 0 .6.04.87.12V9.2c-.28-.07-.58-.11-.87-.11-2.68 0-4.86 2.18-4.86 4.86s2.18 4.86 4.86 4.86c2.68 0 4.86-2.18 4.86-4.86V6.5h1.05c.14 0 .25.11.25.25v1.0c0 2.06 1.67 3.73 3.73 3.73v-1.86c-1.02 0-1.82-.83-1.82-1.85V7.75h-3.6z" fill="currentColor"/>
                </svg>
              </a>
            </div>
          <div className="border border-blue-500 p-1 w-60 mx-auto rounded-full flex items-center justify-between mb-4">
            <span className="font-inter text-xs font-medium text-gray-900 ml-3">
              Explore how to ship with us.
            </span>
            <a
              href="javascript:;"
              className="w-8 h-8 rounded-full flex justify-center items-center bg-blue-500"
            >
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.83398 8.00019L12.9081 8.00019M9.75991 11.778L13.0925 8.44541C13.3023 8.23553 13.4073 8.13059 13.4073 8.00019C13.4073 7.86979 13.3023 7.76485 13.0925 7.55497L9.75991 4.22241"
                  stroke="white"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>

          {/* Mobile social icons under the 'Explore' pill */}
          <div className="flex sm:hidden justify-center gap-3 ">
            <a
              href="https://instagram.com/yourhandle"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/90 shadow"
            >
              <svg className="w-6 h-6 text-pink-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.2"/>
                <circle cx="17.5" cy="6.5" r="0.7" fill="currentColor"/>
              </svg>
            </a>

            <a
              href="https://www.tiktok.com/@yourhandle"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/90 shadow"
            >
              <svg className="w-6 h-6 text-black" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M17.5 7.75c-1.1 0-2.05-.38-2.82-1.02v6.54c0 1.9-1.55 3.45-3.45 3.45-1.9 0-3.45-1.55-3.45-3.45 0-1.9 1.55-3.45 3.45-3.45.3 0 .6.04.87.12V9.2c-.28-.07-.58-.11-.87-.11-2.68 0-4.86 2.18-4.86 4.86s2.18 4.86 4.86 4.86c2.68 0 4.86-2.18 4.86-4.86V6.5h1.05c.14 0 .25.11.25.25v1.0c0 2.06 1.67 3.73 3.73 3.73v-1.86c-1.02 0-1.82-.83-1.82-1.85V7.75h-3.6z" fill="currentColor"/>
              </svg>
            </a>
          </div>

          {/* Hero area: two-column layout — left: hero text, right: promotion */}
          {(() => {
            const activePromo = (promotions && promotions.length > 0) ? promotions[0] : null;
            return (
              <div className="mb-6  md:py-16 min-h-[56vh]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                    {/* Left: hero text (wider) */}
                    <div className="text-left md:col-span-7">
                      <h1 className="text-4xl md:text-5x py-4 lg:text-6xl font-extrabold text-gray-900 mb-5 leading-tight">
                        {heroContent.title}
                        <br/>
                        {heroContent.titleHighlight && (
                          <span className="text-blue-500 py-4"> {heroContent.titleHighlight}</span>
                        )}
                      </h1>
                      {heroContent.subtitle && (
                        <p className="text-lg text-gray-600 mb-5 py-4  max-w-3xl">{heroContent.subtitle}</p>
                      )}

                      <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-3 py-4 mb-6">
                        <Link
                          href={session ? "/dashboard" : "/signup"}
                          className="inline-flex items-center px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition shadow-lg"
                        >
                          {session ? heroContent.ctaTextLoggedIn : heroContent.ctaText}
                        </Link>

                        <WhatsappButton
                          phoneNumber="18767787738"
                          message="Hi! I'm interested in your shipping options. Can you help me?"
                          noDefaultStyles={true}
                          className="inline-flex items-center px-6 py-2 rounded-full border border-blue-600 text-blue-600 font-semibold bg-white hover:bg-blue-50 transition shadow-none w-auto h-auto"
                        />
                      </div>

                      {/* Quick highlights to add vertical rhythm */}
                      <div className="flex flex-col sm:flex-row sm:gap-6 gap-3 items-start text-gray-700">
                        <div className="flex items-center gap-3">
                          <span className="w-3 h-3 rounded-full bg-blue-600" />
                          <span className="text-sm">Door-to-door delivery</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="w-3 h-3 rounded-full bg-blue-600" />
                          <span className="text-sm">Customs & documentation</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="w-3 h-3 rounded-full bg-blue-600" />
                          <span className="text-sm">Transparent pricing</span>
                        </div>
                      </div>
                    </div>

                    {/* Right: promotion image (DB-driven only) */}
                    <div className="md:col-span-5 flex justify-end">
                      <div className="relative rounded-2xl shadow-xl overflow-hidden w-full max-w-[520px] bg-white border border-gray-100">
                        {/* subtle left-side gradient to soften the image against the text */}
                        <div className="absolute left-0 top-0 bottom-0 w-28 z-10 pointer-events-none bg-gradient-to-r from-white to-transparent" />
                        {activePromo ? (
                          activePromo.imageUrl ? (
                            <img
                              src={activePromo.imageUrl}
                              alt={activePromo.title}
                              className="w-full h-72 sm:h-80 md:h-[460px] lg:h-[520px] object-cover filter saturate-95 contrast-95 relative z-0"
                            />
                          ) : (
                            // If promo exists but no image, render empty placeholder
                            <div className="w-full h-72 sm:h-80 md:h-[460px] lg:h-[520px] bg-slate-100" />
                          )
                        ) : (
                          // No promotion configured: keep layout space
                          <div className="w-full h-72 sm:h-80 md:h-[460px] lg:h-[520px]" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}


          <Link
            href={session ? "/dashboard" : "/signup"}
            className="w-full md:hidden mb-14 inline-flex items-center justify-center py-3 px-7 text-base font-semibold text-center text-white rounded-full bg-blue-500 shadow-xs hover:bg-indigo-700 transition-all duration-500"
          >
            {session ? heroContent.ctaTextLoggedIn : heroContent.ctaText}
            <svg
              className="ml-2"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.5 15L11.0858 11.4142C11.7525 10.7475 12.0858 10.4142 12.0858 10C12.0858 9.58579 11.7525 9.25245 11.0858 8.58579L7.5 5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        
        </div>
      </section>

      {/* Satisfied Customers Marquee */}
      <CustomersMarquee />

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              About <span className="text-blue-500">Source It Shipping & Logistics</span>
            </h2>
            <div className="text-gray-600 text-lg max-w-3xl mx-auto">
              <p className="mb-6">
                Source It Shipping & Logistics is a U.S.-based logistics provider specializing in domestic freight, international air and sea cargo, Caribbean and global distribution, and specialized equipment sourcing. We manage packaging, documentation, clearance, and final-mile delivery so you can focus on your business.
              </p>

              <div className="flex items-center justify-center">
                <a href="/about" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg shadow hover:bg-blue-700 transition">
                  Read More
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Services */}
            <div className="bg-blue-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm">✓</span>
                Our Specializations
              </h3>
              <ul className="space-y-3">
                {(aboutSpecializations.length > 0 ? aboutSpecializations : [
                  { _id: "default-1", text: "Freight forwarding" },
                  { _id: "default-2", text: "Home residential delivery services" },
                  { _id: "default-3", text: "Commercial pickup & delivery" },
                  { _id: "default-4", text: "Container sales" },
                  { _id: "default-5", text: "Shipping consultations" },
                  { _id: "default-6", text: "Packing services (barrels, boxes, crates)" },
                  { _id: "default-7", text: "International shipping to the Caribbean" },
                  { _id: "default-8", text: "Customs clearance & final-mile delivery" },
                  {_id: "default-9", text: "We do pick up and ship from Texas (call for rates)" },
                ]).map((service) => (
                  <li key={service._id || service.text} className="flex items-center gap-3 text-gray-700">
                    <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                    {service.text}
                  </li>
                ))}
              </ul>
            </div>

            {/* Destinations */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm">🌍</span>
                We Currently Ship To
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {(shippingDestinations.length > 0 ? shippingDestinations : [
                  { _id: "dest-1", country: "Jamaica", flag: "🇯🇲" },
                  { _id: "dest-2", country: "Trinidad & Tobago", flag: "🇹🇹" },
                  { _id: "dest-3", country: "Suriname", flag: "🇸🇷" },
                  { _id: "dest-4", country: "Curaçao", flag: "🇨🇼" },
                  { _id: "dest-5", country: "Guyana", flag: "🇬🇾" },
                  { _id: "dest-6", country: "Barbados", flag: "🇧🇧" },
                  { _id: "dest-7", country: "Aruba", flag: "🇦🇼" },
                  { _id: "dest-8", country: "China", flag: "🇨🇳" },
                ]).map(({ _id, country, flag }) => (
                  <div key={_id || country} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 shadow-sm">
                    <span className="text-2xl">{flag}</span>
                    <span className="text-gray-800 font-medium text-sm">{country}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* how it works */}
      <section className="py-16  mt-24">
        <div className="max-w-screen-xl mx-auto px-6 md:flex items-center justify-between space-x-12">
          {/* Image Section */}
          <div className="flex-1">
            <Image
              src="/images/work.jpg"
              width={500}
              height={80}
              alt="Dashboard image"
              className="rounded-t-3xl h-auto object-cover"
            />
          </div>

          {/* Steps Section */}
          <div className="flex-1 mt-12 md:mt-0">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              How It Works
            </h2>
            <div className="space-y-8">
              {(howItWorksSteps.length > 0 ? howItWorksSteps : [
                { _id: "step-1", order: 1, title: "Step One", description: "Start by signing up for our service to get access to all our amazing features." },
                { _id: "step-2", order: 2, title: "Step Two", description: "Copy our warehouse address to your favourite shopping site" },
                { _id: "step-3", order: 3, title: "Step Three", description: "You'll get updates right inside your dashboard as to where your package is" },
              ]).map((step, index) => (
                <div key={step._id || index} className="flex items-start space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 min-w-[3rem] min-h-[3rem] bg-blue-500 text-white rounded-full text-lg font-bold">
                    {step.order || index + 1}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 mt-2">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* pricing section */}
      <section id="pricing" className="py-16 ">
        <div className="max-w-4xl mx-auto text-center ">
          <h2 className="text-3xl font-semibold text-blue-500">
            Affordable Shipping Rates
          </h2>
          <p className="text-lg text-gray-600 mt-4">
            Rates are based on package weight/Air & Volumen/Sea.
          </p>


          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              onClick={() => switchPricingService("air")}
              className={`px-4 py-2 rounded-full font-semibold ${pricingService === "air" ? "bg-blue-600 text-white" : "bg-white text-slate-700 border border-slate-200"}`}
            >Air Freight</button>
            <button
              onClick={() => switchPricingService("sea")}
              className={`px-4 py-2 rounded-full font-semibold ${pricingService === "sea" ? "bg-blue-600 text-white" : "bg-white text-slate-700 border border-slate-200"}`}
            >Sea Freight</button>
          </div>

          {/* Pricing Table Section */}
          <div className="mt-8 overflow-x-auto shadow-lg rounded-lg">
            <table className="min-w-full text-left table-auto bg-white border border-gray-200">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="px-6 py-4 text-lg font-semibold">Weight</th>
                  <th className="px-6 py-4 text-lg font-semibold">Cost</th>
                </tr>
              </thead>
              <tbody>
                {(pricingRows.length > 0 ? pricingRows : DEFAULT_PUBLIC_PRICING.filter(p => p.serviceType === pricingService)).map((row) => (
                  <tr key={row._id || row.weight} className={`border-b ${row.isHighlighted ? "bg-blue-500" : ""}`}>
                    <td className={`px-6 py-4 ${row.isHighlighted ? "text-white" : "text-gray-800"}`}>{row.weight}</td>
                    <td className={`px-6 py-4 ${row.isHighlighted ? "text-white" : "text-gray-800"}`}>{row.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>


      <ProfileForm />

      {/* Partners Section */}
      <PartnersSection />

     

      {/* Services Section - Now using database data */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-800 text-center sm:text-4xl mb-4">
            Our Specialized Services
          </h2>
          <p className="text-center text-blue-600 font-semibold mb-2">
            Powered by Source it Shipping & Logistics LLC
          </p>
          <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12">
            We provide comprehensive clearance and delivery services for
            vehicles, household items, groceries, and cargo with reliable
            door-to-door service across Jamaica.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div 
                key={service._id} 
                className={`bg-white rounded-lg hover:scale-105 ease-linear duration-300 shadow-lg p-6 text-center border-t-4 border-${service.borderColor}-600 overflow-hidden`}
              >
                <div className="mb-6">
                  {service.videoUrl ? (
                    <video
                      src={service.videoUrl}
                      controls
                      className="w-full h-48 object-cover rounded-lg mb-4 bg-black"
                    />
                  ) : (
                    <img
                      src={service.imageUrl}
                      alt={service.title}
                      className="w-full h-48 object-cover rounded-lg mb-4"
                    />
                  )}
                  <div className="flex justify-center items-center mb-2">
                    <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-${service.iconColor}-100`}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-6 w-6 text-${service.iconColor}-600`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          {/* Features Section */}
          <div className="mt-16 bg-white rounded-lg shadow-md p-8">
            <h3 className="text-2xl font-bold text-gray-800 text-center mb-8">
              Why Choose Our Services?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-4">
                  <svg
                    className="h-6 w-6 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-gray-800">
                  Licensed & Bonded
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Fully licensed customs brokers
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                  <svg
                    className="h-6 w-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-gray-800">Fast Processing</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Quick clearance times
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 mb-4">
                  <svg
                    className="h-6 w-6 text-orange-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-gray-800">Expert Team</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Experienced professionals
                </p>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 mb-4">
                  <svg
                    className="h-6 w-6 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <h4 className="font-semibold text-gray-800">24/7 Support</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Always here to help
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Membership Benefits Section - Now using database data */}
      <section className="py-16 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Exclusive Membership Benefits
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Join our shipping club and enjoy premium benefits all year round
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div key={benefit._id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 bg-${benefit.iconColor}-100 rounded-full flex items-center justify-center`}>
                      <div className={`w-4 h-4 bg-${benefit.iconColor}-600 rounded-full`}></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">{benefit.title}</h3>
                    <p className="text-sm text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              href={session ? "/dashboard/membership" : "/signup"}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105"
            >
              {session ? "Manage Membership" : "Become a Member Today"}
              <svg
                className="ml-2 w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* testimonal */}
      <TestimonialSection dbTestimonials={testimonials} />

      {/* FAQS */}
      <FAQS faqs={faqs} />

      {/* Forms and Documents */}
      <FormsAndDocuments />

      {/* Company Information Section - Now using database data */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Source it Shipping & Logistics LLC
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your trusted partner for comprehensive shipping and logistics solutions between Jamaica and the United States.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-12">
            {addresses.map((address, index) => (
              <div 
                key={address._id} 
                className={`bg-white rounded-xl shadow-lg p-8 border-t-4 ${index === 0 ? 'border-blue-500' : 'border-green-500'}`}
              >
                <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                  <svg 
                    className={`w-6 h-6 mr-2 ${index === 0 ? 'text-blue-500' : 'text-green-500'}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {address.country} Address
                </h3>
                <div className="text-gray-600 space-y-2">
                  <p className="font-medium">{address.address}</p>
                  {address.address === "Will be provided upon request" && (
                    <p className="text-sm">Contact us for shipping address details</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    <footer className="bg-slate-900 py-12 text-white">
      <div className="max-w-6xl mx-auto px-6 text-center space-y-8">
        {/* CTA Section */}
   
 

        {/* Contact Info */}
        <div className="pt-6 ">
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-8 text-slate-300">
            <div className="flex items-center space-x-2">
             
              <WhatsappButton 
  phoneNumber="18767787738" 
  message="Hi! I want to know more about pricing."
/>
            </div>
            <div className="flex items-center space-x-2">
            
              <EmailButton email="sourceitshipping@gmail.com" />
            </div>
          </div>
        </div>

     

        {/* Copyright */}
        <div className="pt-6 border-t border-slate-700 text-slate-400 text-sm">
          <p>&copy; 2025 Source It Shipping and Logistic. All rights reserved. | Fast, Reliable, Trusted.</p>
        </div>
      </div>
    </footer>

    </div>
  );
};

export default Home;